import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";
import DictationPage from "@/pages/DictationPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import LandingPage from "@/pages/LandingPage";
import SignupPage from "@/pages/SignupPage";
import TermsPage from "@/pages/TermsPage";
import DownloadPage from "@/pages/DownloadPage";
import SplashScreen from "@/components/SplashScreen";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Check if running as installed PWA (standalone mode)
const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                     window.navigator.standalone === true;

// Restore saved window size for PWA, or set default 20% size
if (isStandalone) {
  try {
    const savedSize = localStorage.getItem('window_size');
    if (savedSize) {
      // Restore user's preferred size
      const { width, height } = JSON.parse(savedSize);
      window.resizeTo(width, height);
      console.log('Restored window size:', width, 'x', height);
    } else if (window.outerWidth > 500) {
      // First time: set to 20% in upper right
      const screenWidth = window.screen.availWidth;
      const screenHeight = window.screen.availHeight;
      const targetWidth = Math.max(380, Math.round(screenWidth * 0.20));
      const targetHeight = Math.max(600, Math.round(screenHeight * 0.45));
      const targetX = screenWidth - targetWidth - 20;
      const targetY = 20;
      
      window.resizeTo(targetWidth, targetHeight);
      window.moveTo(targetX, targetY);
      console.log('Set initial window size:', targetWidth, 'x', targetHeight);
    }
  } catch (e) {
    console.log('Window resize not available:', e.message);
  }
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if this is the auth callback with session_id
  const isAuthCallback = location.hash?.includes('session_id=') || location.pathname === '/auth/callback';

  // Check if running as installed PWA
  const isStandalonePWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Check authentication on mount
  useEffect(() => {
    if (isAuthCallback) {
      setIsLoading(false);
      setAuthChecked(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        // User is already logged in - show splash screen every time they open the app
        if (response.data) {
          setShowSplash(true);
        }
      } catch (error) {
        setUser(null);
      } finally {
        setIsLoading(false);
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, [isAuthCallback]);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    // Show splash screen after fresh login too
    setShowSplash(true);
    navigate("/", { replace: true });
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors
    }
    // Reset resize hint so it shows again on next login
    localStorage.removeItem('resize_hint_dismissed');
    // Clear session storage for install banner
    sessionStorage.removeItem('pwa_install_dismissed');
    setUser(null);
  };

  const handleSplashComplete = () => {
    setShowSplash(false);
    // No localStorage - splash shows every time app opens
  };

  // Show loading state while checking auth - matches splash screen background
  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#0f1419' }}
      >
        {/* Minimal loading - just the background, no spinner to avoid jarring transition */}
      </div>
    );
  }

  // Handle auth callback (Google sign-in return)
  if (isAuthCallback) {
    return <AuthCallback onAuthSuccess={handleAuthSuccess} />;
  }

  // Show splash screen EVERY TIME user opens app (when logged in)
  // Skip splash for download page
  if (showSplash && user && location.pathname !== '/download') {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  return (
    <Routes>
      {/* Landing Page - for non-authenticated visitors */}
      <Route 
        path="/" 
        element={
          user ? (
            <DictationPage user={user} onLogout={handleLogout} />
          ) : (
            <LandingPage />
          )
        } 
      />
      
      {/* App - requires authentication */}
      <Route 
        path="/app" 
        element={
          user ? (
            <DictationPage user={user} onLogout={handleLogout} />
          ) : (
            <AuthPage onAuthSuccess={handleAuthSuccess} />
          )
        } 
      />
      
      {/* Auth Page - login/signup */}
      <Route 
        path="/auth" 
        element={
          user ? (
            <DictationPage user={user} onLogout={handleLogout} />
          ) : (
            <AuthPage onAuthSuccess={handleAuthSuccess} />
          )
        } 
      />
      
      {/* New Signup Flow with Phone Verification */}
      <Route 
        path="/signup" 
        element={<SignupPage />} 
      />
      
      {/* Terms & Conditions */}
      <Route 
        path="/terms" 
        element={<TermsPage />} 
      />
      
      {/* Auth Callback for Google OAuth */}
      <Route 
        path="/auth/callback" 
        element={<AuthCallback onAuthSuccess={handleAuthSuccess} />} 
      />
      
      {/* Password Reset */}
      <Route 
        path="/reset-password" 
        element={<ResetPasswordPage />} 
      />
      
      {/* Download Page - shown after signup, requires auth */}
      <Route 
        path="/download" 
        element={<DownloadPage />} 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
